import {
  Bundle as BundleProto,
  BundleBaseItem as BundleBaseItemProto,
  BundleIncludedItem as BundleIncludedItemProto,
} from '@taxfyle/api-internal/internal/bundle_pb'
import { Model } from '../../infra/model'
import { Id } from '../../types/definitions'
import { makeObservable, observable } from 'mobx'
import { decimalToNumber, timestampToISO } from 'utils/grpc-util'
import once from 'lodash/once'
import { DateZero, maybeParseDate } from 'utils/date-util'
import User from 'iam/models/User'
import { PrimeUser } from 'iam/UserStore'
import Workspace from 'iam/models/Workspace'
import { PrimeWorkspace } from 'iam/WorkspaceStore'

const BundleLazyNone = once(
  () =>
    new Bundle(
      () => User.none,
      () => Workspace.none
    )
)

export class Bundle extends Model<BundleProto.AsObject, Bundle> {
  @observable Id = Id.none
  @observable workspace: Workspace = Workspace.none
  @observable archived = false
  @observable name = ''
  @observable description = ''
  @observable price = 0
  @observable createdBy = User.none
  @observable dateCreated = DateZero()
  @observable includedItems: BundleIncludedItem[] = []
  @observable archivedBy: User | null = null
  @observable dateArchived: Date | null = null
  @observable lastEditedBy: User | null = null
  @observable dateLastEdited: Date | null = null
  @observable baseItem: BundleBaseItem = BundleBaseItem.none

  constructor(
    private primeUser: PrimeUser,
    private primeWorkspace: PrimeWorkspace
  ) {
    super()

    makeObservable(this)
  }

  parse(json: Partial<BundleProto.AsObject>): Partial<Bundle> {
    const dateCreatedString = timestampToISO(json.dateCreated)
    const dateArchivedString = timestampToISO(json.dateArchived)
    const dateLastEditedString = timestampToISO(json.dateLastEdited)
    return {
      Id: json.id,
      workspace: this.primeWorkspace({
        id: json.workspaceId,
      }),
      archived: json.archived || false,
      name: json.name,
      description: json.description,
      price: json.price ? decimalToNumber(json.price) : undefined,
      createdBy: this.primeUser({
        public_id: json.createdBy,
      }),
      dateCreated: maybeParseDate(dateCreatedString),
      includedItems: handleBaseAndIncludedItems(json),
      archivedBy: json.archivedBy
        ? this.primeUser({
            public_id: json.archivedBy.value,
          })
        : null,
      dateArchived: json.dateArchived
        ? maybeParseDate(dateArchivedString)
        : null,
      lastEditedBy: json.lastEditedBy
        ? this.primeUser({
            public_id: json.lastEditedBy.value,
          })
        : null,
      dateLastEdited: json.dateLastEdited
        ? maybeParseDate(dateLastEditedString)
        : null,
      baseItem: BundleBaseItem.from(json.baseItem),
    }
  }

  static get none() {
    return BundleLazyNone()
  }
}

const BundleBaseItemLazyNone = once(
  () => new BundleBaseItem(Id.none, Id.none, '', 0, 0, '', Id.none)
)

export class BundleBaseItem extends Model<
  BundleBaseItemProto.AsObject,
  BundleBaseItem
> {
  @observable inventoryItemId = Id.none
  @observable inventoryItemVersionId = Id.none
  @observable name = ''
  @observable price? = 0
  @observable proPrice? = 0
  @observable description = ''
  @observable workspaceId? = Id.none

  constructor(
    inventoryItemId: Id,
    inventoryItemVersionId: Id,
    name: string,
    price: number,
    proPrice: number,
    description: string,
    workspaceId: Id
  ) {
    super()
    makeObservable(this)

    this.inventoryItemId = inventoryItemId
    this.inventoryItemVersionId = inventoryItemVersionId
    this.name = name
    this.price = price
    this.proPrice = proPrice
    this.description = description
    this.workspaceId = workspaceId
  }

  parse(json: Partial<BundleBaseItemProto.AsObject>): Partial<BundleBaseItem> {
    return {
      inventoryItemId: json.inventoryItemId,
      inventoryItemVersionId: json.inventoryItemVersionId,
      name: json.name,
      price: json.price ? decimalToNumber(json.price) : undefined,
      proPrice: json.proPrice ? decimalToNumber(json.proPrice) : undefined,
      description: json.description,
      workspaceId: json.workspaceId?.value || '',
    }
  }

  static from(attrs?: BundleBaseItemProto.AsObject): BundleBaseItem {
    if (!attrs) {
      return BundleBaseItem.none
    }
    return new BundleBaseItem(
      attrs.inventoryItemId,
      attrs.inventoryItemVersionId,
      attrs.name,
      attrs.price ? decimalToNumber(attrs.price) : 0,
      attrs.proPrice ? decimalToNumber(attrs.proPrice) : 0,
      attrs.description,
      attrs.workspaceId?.value || ''
    )
  }

  static get none() {
    return BundleBaseItemLazyNone()
  }
}

export class BundleIncludedItem extends Model<
  BundleIncludedItemProto.AsObject,
  BundleIncludedItem
> {
  @observable inventoryItemId = Id.none
  @observable inventoryItemVersionId = Id.none
  @observable name = ''
  @observable price = 0
  @observable proPrice = 0
  @observable description = ''
  @observable workspaceId = Id.none
  @observable includedQuantity = 0
  @observable isBaseItem = false

  constructor(
    inventoryItemId: Id,
    inventoryItemVersionId: Id,
    name: string,
    price: number,
    proPrice: number,
    description: string,
    workspaceId: Id,
    includedQuantity: number,
    isBaseItem: boolean
  ) {
    super()
    makeObservable(this)

    this.inventoryItemId = inventoryItemId
    this.inventoryItemVersionId = inventoryItemVersionId
    this.name = name
    this.price = price
    this.proPrice = proPrice
    this.description = description
    this.workspaceId = workspaceId
    this.includedQuantity = includedQuantity
    this.isBaseItem = isBaseItem
  }

  parse(
    json: Partial<BundleIncludedItemProto.AsObject>
  ): Partial<BundleIncludedItem> {
    return {
      inventoryItemId: json.inventoryItemId,
      inventoryItemVersionId: json.inventoryItemVersionId,
      name: json.name,
      price: json.price ? decimalToNumber(json.price) : undefined,
      proPrice: json.proPrice ? decimalToNumber(json.proPrice) : undefined,
      description: json.description,
      workspaceId: json.workspaceId?.value || '',
      includedQuantity: json.includedQuantity || 0,
    }
  }

  static fromIncludedItem(
    attrs: BundleIncludedItemProto.AsObject
  ): BundleIncludedItem {
    return new BundleIncludedItem(
      attrs.inventoryItemId,
      attrs.inventoryItemVersionId,
      attrs.name,
      attrs.price ? decimalToNumber(attrs.price) : 0,
      attrs.proPrice ? decimalToNumber(attrs.proPrice) : 0,
      attrs.description,
      attrs.workspaceId?.value || '',
      attrs.includedQuantity || 0,
      false
    )
  }

  static fromBaseItem(attrs: BundleBaseItemProto.AsObject): BundleIncludedItem {
    return new BundleIncludedItem(
      attrs.inventoryItemId,
      attrs.inventoryItemVersionId,
      attrs.name,
      attrs.price ? decimalToNumber(attrs.price) : 0,
      attrs.proPrice ? decimalToNumber(attrs.proPrice) : 0,
      attrs.description,
      attrs.workspaceId?.value || '',
      1,
      true
    )
  }
}

function handleBaseAndIncludedItems(
  json: Partial<BundleProto.AsObject>
): BundleIncludedItem[] {
  if (!json.baseItem || !json.includedItemsList) {
    return []
  }

  const includedItems = json.includedItemsList.map(
    (attrs: BundleIncludedItemProto.AsObject) =>
      BundleIncludedItem.fromIncludedItem(attrs)
  )

  const includedBaseItem = BundleIncludedItem.fromBaseItem(json.baseItem)

  return [includedBaseItem, ...includedItems]
}
